import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http, inr, requestErrorMessage } from "@/lib/erp";
import { TAX_REPORTS } from "@/lib/gstReports";
import { downloadCSV } from "@/lib/erpdocs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Download, Percent, Printer } from "lucide-react";
import PrintBrandHeader from "@/components/PrintBrandHeader";

const SummaryCard = ({ title, s }) => (
  <Card className="p-4">
    <div className="text-xs uppercase tracking-wider text-muted-foreground">{title}</div>
    <div className="font-heading text-xl font-bold mt-1 tabular">{inr(s.total)}</div>
    <div className="text-xs text-muted-foreground mt-1">{s.count} invoices · Tax {inr(s.cgst + s.sgst + s.igst)}</div>
  </Card>
);

const Empty = ({ cols, icon: Icon = Percent, label = "No records" }) => (
  <tr><td colSpan={cols} className="px-4 py-10 text-center text-muted-foreground"><Icon className="h-8 w-8 mx-auto mb-2 opacity-40" />{label}</td></tr>
);

const Th = ({ children, right }) => <th className={`px-3 py-2 font-medium ${right ? "text-right" : ""}`}>{children}</th>;
const Td = ({ children, right, mono, className = "" }) => <td className={`px-3 py-2 ${right ? "text-right" : ""} ${mono ? "font-mono" : ""} ${className}`}>{children}</td>;

// Simple deduction-style report (TDS/TCS receivable & payable)
const DeductionReport = ({ data, partyLabel, exportName }) => {
  const rows = data?.rows || [];
  const doExport = () => downloadCSV(exportName, ["Ref No", "Date", partyLabel, "Base Amount", "Amount"],
    rows.map((r) => [r.ref_no, r.date, r.party, r.base, r.amount]));
  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">Total: <span className="font-mono font-semibold text-foreground">{inr(data?.total || 0)}</span></div>
        <Button size="sm" variant="outline" onClick={doExport} disabled={!rows.length} data-testid={`export-${exportName}`}><Download className="h-4 w-4 mr-1" /> Export CSV</Button>
      </div>
      <Card className="p-0 overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">
        <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
          <Th>Ref No</Th><Th>Date</Th><Th>{partyLabel}</Th><Th right>Base Amount</Th><Th right>Amount</Th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (<tr key={i} className="border-t">
            <Td mono>{r.ref_no}</Td><Td>{r.date}</Td><Td>{r.party}</Td>
            <Td right mono>{inr(r.base)}</Td><Td right mono className="font-semibold">{inr(r.amount)}</Td>
          </tr>))}
          {rows.length === 0 && <Empty cols={5} />}
        </tbody>
      </table></div></Card>
    </div>
  );
};

export default function GSTReports() {
  const [month, setMonth] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedReport = searchParams.get("report");
  const tab = TAX_REPORTS.some(([key]) => key === selectedReport) ? selectedReport : "gstr1";
  const setTab = (value) => setSearchParams((current) => {
    const next = new URLSearchParams(current);
    next.set("report", value);
    return next;
  });
  const [g, setG] = useState(null);
  const [hsn, setHsn] = useState(null);
  const [rep, setRep] = useState(null);
  const [company, setCompany] = useState(null);
  const [error, setError] = useState("");
  const [companyError, setCompanyError] = useState("");
  const [loading, setLoading] = useState(true);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    const params = month ? { month } : {};
    setError("");
    setLoading(true);
    setG(null);
    setHsn(null);
    setRep(null);
    Promise.all([
      http.get("/gst/gstr1", { params }),
      http.get("/gst/hsn", { params }),
      http.get("/gst/reports", { params }),
    ]).then(([sales, hsnResult, reports]) => {
      if (!active) return;
      if (!sales.data || !hsnResult.data || !reports.data) {
        setError("Tax report data was empty. Please retry.");
        return;
      }
      setG(sales.data);
      setHsn(hsnResult.data);
      setRep(reports.data);
    }).catch((requestError) => {
      if (active) setError(requestErrorMessage(requestError, "load tax reports"));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [month, retry]);
  useEffect(() => {
    let active = true;
    http.get("/settings")
      .then((response) => { if (active) setCompany(response.data); })
      .catch((requestError) => {
        if (active) setCompanyError(requestErrorMessage(requestError, "load company details"));
      });
    return () => { active = false; };
  }, []);

  if (error) return (
    <div className="p-8 space-y-3" role="alert">
      <p className="text-rose-700">{error}</p>
      <Button size="sm" variant="outline" onClick={() => setRetry((value) => value + 1)}>Retry</Button>
    </div>
  );
  if (loading || !g || !hsn || !rep) return <div className="p-8 text-muted-foreground">Loading tax data…</div>;

  const exportGstr1 = () => {
    const rows = [...g.b2b.map((r) => ["B2B", r.invoice_no, r.date, r.customer, r.gstin, r.pos, r.taxable, r.cgst, r.sgst, r.igst, r.total]),
      ...g.b2c.map((r) => ["B2C", r.invoice_no, r.date, r.customer, r.gstin, r.pos, r.taxable, r.cgst, r.sgst, r.igst, r.total])];
    downloadCSV(`GSTR1_${g.month}.csv`, ["Type", "Invoice", "Date", "Customer", "GSTIN", "POS", "Taxable", "CGST", "SGST", "IGST", "Total"], rows);
  };
  const exportHsn = () => downloadCSV(`HSN_${hsn.month}.csv`, ["HSN", "Qty", "Taxable", "CGST", "SGST", "IGST", "Total"],
    hsn.rows.map((r) => [r.hsn, r.qty, r.taxable, r.cgst, r.sgst, r.igst, r.total]));
  const exportGstr2b = () => downloadCSV(`Purchase_Register_${rep.month}.csv`, ["Doc No", "Bill No", "Date", "Vendor", "GSTIN", "Taxable", "CGST", "SGST", "IGST", "Total"],
    rep.gstr2b.rows.map((r) => [r.doc_no, r.bill_no, r.date, r.vendor, r.gstin, r.taxable, r.cgst, r.sgst, r.igst, r.total]));

  const SalesTbl = ({ rows }) => (
    <div className="overflow-x-auto"><table className="w-full text-sm">
      <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
        <Th>Invoice</Th><Th>Date</Th><Th>Customer</Th><Th>GSTIN</Th><Th right>Taxable</Th><Th right>CGST</Th><Th right>SGST</Th><Th right>IGST</Th><Th right>Total</Th>
      </tr></thead>
      <tbody>{rows.map((r, i) => (<tr key={i} className="border-t">
        <Td mono>{r.invoice_no}</Td><Td>{r.date}</Td><Td>{r.customer}</Td><Td mono className="text-xs">{r.gstin || "—"}</Td>
        <Td right mono>{inr(r.taxable)}</Td><Td right mono>{inr(r.cgst)}</Td><Td right mono>{inr(r.sgst)}</Td><Td right mono>{inr(r.igst)}</Td><Td right mono className="font-semibold">{inr(r.total)}</Td>
      </tr>))}{rows.length === 0 && <Empty cols={9} />}</tbody>
    </table></div>
  );

  const b = rep.gstr3b;
  const Line3B = ({ label, s, strong }) => (
    <div className={`grid grid-cols-5 gap-2 px-4 py-3 text-sm ${strong ? "font-semibold bg-brand-50/60" : "border-t"}`}>
      <div className="text-muted-foreground">{label}</div>
      <div className="text-right font-mono">{inr(s.cgst)}</div>
      <div className="text-right font-mono">{inr(s.sgst)}</div>
      <div className="text-right font-mono">{inr(s.igst)}</div>
      <div className="text-right font-mono">{inr(s.total)}</div>
    </div>
  );

  return (
    <div id="print-area" className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PrintBrandHeader company={company} title="GST Report" subtitle={`Period: ${rep.month}`} />
      {companyError && <p className="text-sm text-amber-700" role="status">{companyError}. Tax report data is still available.</p>}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">GST Center</h1><p className="text-sm text-muted-foreground">Taxes & returns · {rep.month}</p></div>
        <div className="flex items-center gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="gst-month-input" placeholder="All periods" />
          <Button size="sm" variant="print" onClick={() => window.print()} data-testid="gst-print-button"><Printer className="h-4 w-4 mr-1" /> Print</Button>
        </div>
      </div>
      <div className="space-y-2">
        <Link to="/reports/taxes" className="no-print text-sm text-brand-700 hover:underline">← All tax reports</Link>
        <Card className="p-3 text-sm text-amber-900 bg-amber-50 border-amber-200">
          Internal transaction summaries only. They are not portal-verified or filing-ready; reconcile returns,
          credit notes, GST portal statements and tax eligibility with your accountant before filing.
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto justify-start">
          {TAX_REPORTS.map(([k, label]) => (
            <TabsTrigger key={k} value={k} data-testid={`tab-${k}`}>{label}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="gstr1" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <SummaryCard title="B2B (Registered)" s={g.b2b_summary} />
            <SummaryCard title="B2C (Unregistered)" s={g.b2c_summary} />
            <SummaryCard title="Total Outward" s={g.total_summary} />
          </div>
          <div className="flex justify-end"><Button size="sm" variant="outline" onClick={exportGstr1} data-testid="export-gstr1"><Download className="h-4 w-4 mr-1" /> Export GSTR-1 CSV</Button></div>
          <Card className="p-0 overflow-hidden"><div className="px-4 py-3 border-b font-heading font-semibold text-sm">B2B — Business to Business</div><SalesTbl rows={g.b2b} /></Card>
          <Card className="p-0 overflow-hidden"><div className="px-4 py-3 border-b font-heading font-semibold text-sm">B2C — Business to Consumer</div><SalesTbl rows={g.b2c} /></Card>
        </TabsContent>

        <TabsContent value="gstr2b" className="space-y-4 mt-4">
          <Card className="p-3 text-sm text-amber-900 bg-amber-50 border-amber-200">
            This is your purchase register, not the official GSTR-2B statement. Input tax has not been reconciled with the GST portal or checked for eligibility.
          </Card>
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">Input tax recorded · <span className="font-mono font-semibold text-foreground">{inr(rep.gstr2b.summary.total)}</span> ({rep.gstr2b.summary.count} bills)</div>
            <Button size="sm" variant="outline" onClick={exportGstr2b} disabled={!rep.gstr2b.rows.length} data-testid="export-gstr2b"><Download className="h-4 w-4 mr-1" /> Export CSV</Button>
          </div>
          <Card className="p-0 overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <Th>Doc No</Th><Th>Bill No</Th><Th>Date</Th><Th>Vendor</Th><Th>GSTIN</Th><Th right>Taxable</Th><Th right>CGST</Th><Th right>SGST</Th><Th right>IGST</Th><Th right>Total</Th>
            </tr></thead>
            <tbody>{rep.gstr2b.rows.map((r, i) => (<tr key={i} className="border-t">
              <Td mono>{r.doc_no}</Td><Td mono className="text-xs">{r.bill_no || "—"}</Td><Td>{r.date}</Td><Td>{r.vendor}</Td><Td mono className="text-xs">{r.gstin || "—"}</Td>
              <Td right mono>{inr(r.taxable)}</Td><Td right mono>{inr(r.cgst)}</Td><Td right mono>{inr(r.sgst)}</Td><Td right mono>{inr(r.igst)}</Td><Td right mono className="font-semibold">{inr(r.total)}</Td>
            </tr>))}{rep.gstr2b.rows.length === 0 && <Empty cols={10} />}</tbody>
          </table></div></Card>
        </TabsContent>

        <TabsContent value="gstr3b" className="space-y-4 mt-4">
          <Card className="p-3 text-sm text-amber-900 bg-amber-50 border-amber-200">
            Estimated from recorded sales and purchases. ITC eligibility, reversals and other filing adjustments are not verified.
          </Card>
          <Card className="p-0 overflow-hidden">
            <div className="grid grid-cols-5 gap-2 px-4 py-3 bg-slate-50 text-xs uppercase tracking-wider text-muted-foreground font-medium">
              <div>Particulars</div><div className="text-right">CGST</div><div className="text-right">SGST</div><div className="text-right">IGST</div><div className="text-right">Total Tax</div>
            </div>
            <Line3B label="Outward Tax Liability" s={b.outward} />
            <Line3B label="Recorded Input Tax (Inward)" s={b.itc} />
            <Line3B label="Estimated Net Tax" s={b.net} strong />
          </Card>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="p-4"><div className="text-xs uppercase tracking-wider text-muted-foreground">Outward Taxable</div><div className="font-heading text-xl font-bold mt-1 tabular">{inr(b.outward.taxable)}</div></Card>
            <Card className="p-4"><div className="text-xs uppercase tracking-wider text-muted-foreground">ITC Taxable (Inward)</div><div className="font-heading text-xl font-bold mt-1 tabular">{inr(b.itc.taxable)}</div></Card>
            <Card className="p-4"><div className="text-xs uppercase tracking-wider text-muted-foreground">Estimated Net Tax</div><div className="font-heading text-xl font-bold mt-1 tabular">{inr(b.net.total)}</div></Card>
          </div>
        </TabsContent>

        <TabsContent value="gstr7" className="space-y-4 mt-4">
          <Card className="p-5 text-sm space-y-2">
            <div className="font-heading font-semibold">GST TDS data is not available</div>
            <p className="text-muted-foreground">
              GSTR-7 reports GST tax deducted at source by the deductor. The TDS on sales and purchase invoices in this app is not a GST TDS record, so it cannot be used to prepare GSTR-7. Portal or dedicated GST TDS records are required.
            </p>
          </Card>
        </TabsContent>

        <TabsContent value="hsn" className="space-y-4 mt-4">
          <div className="flex justify-end"><Button size="sm" variant="outline" onClick={exportHsn} data-testid="export-hsn"><Download className="h-4 w-4 mr-1" /> Export HSN CSV</Button></div>
          <Card className="p-0 overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground"><Th>HSN/SAC</Th><Th right>Qty</Th><Th right>Taxable</Th><Th right>CGST</Th><Th right>SGST</Th><Th right>IGST</Th><Th right>Total</Th></tr></thead>
            <tbody>{hsn.rows.map((r, i) => (<tr key={i} className="border-t"><Td mono>{r.hsn}</Td><Td right mono>{r.qty}</Td><Td right mono>{inr(r.taxable)}</Td><Td right mono>{inr(r.cgst)}</Td><Td right mono>{inr(r.sgst)}</Td><Td right mono>{inr(r.igst)}</Td><Td right mono className="font-semibold">{inr(r.total)}</Td></tr>))}
            {hsn.rows.length === 0 && <Empty cols={7} />}</tbody>
          </table></div></Card>
        </TabsContent>

        <TabsContent value="tds_receivable"><DeductionReport data={rep.tds_receivable} partyLabel="Customer" exportName={`TDS_Receivable_${rep.month}.csv`} /></TabsContent>
        <TabsContent value="tds_payable"><DeductionReport data={rep.tds_payable} partyLabel="Vendor" exportName={`TDS_Payable_${rep.month}.csv`} /></TabsContent>
        <TabsContent value="tcs_receivable"><DeductionReport data={rep.tcs_receivable} partyLabel="Vendor" exportName={`TCS_Receivable_${rep.month}.csv`} /></TabsContent>
        <TabsContent value="tcs_payable"><DeductionReport data={rep.tcs_payable} partyLabel="Customer" exportName={`TCS_Payable_${rep.month}.csv`} /></TabsContent>
      </Tabs>
    </div>
  );
}
